import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { InventoryAdjustmentDetail } from '../../../../types/inventory/adjustment/InventoryAdjustmentDetail';
import { InventoryAdjustmentFormValues } from '../../../../types/inventory/adjustment/nventoryAdjustmentFormValues';

interface InventoryAdjustmentState {
  form: InventoryAdjustmentFormValues;
  selectedDetail: InventoryAdjustmentDetail | null;

  formErrors: Record<string, string>;
  isSubmitting: boolean;

  adjustmentSaved: boolean;
  adjustmentLoaded: boolean;
}

const initialFormState: InventoryAdjustmentFormValues = {
  id: null,
  branchId: null,
  reason: '',
  status: 'CREATED',
  details: [],
};

const initialState: InventoryAdjustmentState = {
  form: initialFormState,
  selectedDetail: null,

  formErrors: {},
  isSubmitting: false,

  adjustmentSaved: false,
  adjustmentLoaded: false,
};

const inventoryAdjustmentSlice = createSlice({
  name: 'inventoryAdjustment',
  initialState,
  reducers: {
    /* FLAGS */
    setIsSubmitting(state, action: PayloadAction<boolean>) {
      state.isSubmitting = action.payload;
    },

    setAdjustmentSaved(state, action: PayloadAction<boolean>) {
      state.adjustmentSaved = action.payload;
    },

    setAdjustmentLoaded(state, action: PayloadAction<boolean>) {
      state.adjustmentLoaded = action.payload;
    },

    /* ERRORS */
    clearFormErrors(state) {
      state.formErrors = {};
    },

    setFormErrors(state, action: PayloadAction<Record<string, string>>) {
      state.formErrors = action.payload;
    },

    /* FORM (inputs locales) */
    updateAdjustmentField(
      state,
      action: PayloadAction<{
        field: keyof InventoryAdjustmentFormValues;
        value: any;
      }>
    ) {
      const { field, value } = action.payload;
      state.form[field] = value;
    },

    /* SELECTED DETAIL (para edición) */
    updateSelectedAdjustmentDetailField(
      state,
      action: PayloadAction<{
        field: keyof InventoryAdjustmentDetail;
        value: any;
      }>
    ) {
      if (!state.selectedDetail) return;

      const { field, value } = action.payload;
      state.selectedDetail = {
        ...state.selectedDetail,
        [field]: value,
      };
    },


    /* 🔥 FORM COMPLETO DESDE BACKEND */
    setAdjustmentForm(
      state,
      action: PayloadAction<InventoryAdjustmentFormValues>
    ) {
      state.form = {
        ...initialFormState,
        ...action.payload,
        details: action.payload.details ?? [],
      };

      state.formErrors = {};
    },

    /* RESET */
    clearAdjustmentForm(state) {
      state.form = initialFormState;
      state.formErrors = {};
      state.isSubmitting = false;
      state.adjustmentSaved = false;
      state.adjustmentLoaded = false;
      state.selectedDetail = null;
    },

    /* CLEAR SELECTED DETAIL (cerrar edición) */
    clearSelectedAdjustmentDetail(state) {
      state.selectedDetail = null;
    },


    /* SELECTED DETAIL (para edición) */
    setSelectedAdjustmentDetail(
      state,
      action: PayloadAction<InventoryAdjustmentDetail | null>
    ) {
      state.selectedDetail = action.payload;
    },


  },
});

export const {
  setIsSubmitting,
  setAdjustmentSaved,
  setAdjustmentLoaded,
  clearFormErrors,
  setFormErrors,
  updateAdjustmentField,
  updateSelectedAdjustmentDetailField,
  setAdjustmentForm,
  clearAdjustmentForm,
  clearSelectedAdjustmentDetail,
  setSelectedAdjustmentDetail,
} = inventoryAdjustmentSlice.actions;

export default inventoryAdjustmentSlice.reducer;
